import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../../hooks/useToast';
import {
  ImportRemovalMode,
  ParsedExercise,
  RemovalMode,
  RemovalPreviewExercise,
  ValidationIssue,
} from './types';
import { trpc } from '../../../../trpc/react';
import { environment } from '../../../../environment';
import {
  downloadCuiExportZip,
  downloadFile,
  exercisesToCSV,
  parseCSV,
  toExerciseInput,
} from './utils';

export interface RemovalPreviewState {
  count: number;
  exercises: RemovalPreviewExercise[];
}

export function useExerciseLibraryManagement() {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [exercises, setExercises] = useState<ParsedExercise[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationComplete, setValidationComplete] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Active confirm path mode. Table bulk-delete sets `select_only`; Import
  // dialog confirm sets `replace`.
  const [removalMode, setRemovalModeState] = useState<RemovalMode | null>(null);
  // Import-dialog radio selection — kept separate so it never clobbers the
  // table's select_only selection / confirm path.
  const [importRemovalMode, setImportRemovalModeState] =
    useState<ImportRemovalMode | null>(null);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState<string[]>([]);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removalError, setRemovalError] = useState<string | null>(null);
  // Sync guard — React state alone can lag a double-click before re-render
  // disables the confirm button.
  const isRemovingRef = useRef(false);
  // Frozen at Continue-to-confirm so Retry / stacked dialogs cannot diverge
  // from what the user reviewed. `importCompleted` skips re-import after a
  // removal-step failure (import is not idempotent enough to re-run blindly
  // for Retry — create/update already applied).
  const confirmSnapshotRef = useRef<{
    mode: 'replace';
    exerciseInputs: ReturnType<typeof toExerciseInput>[];
    importCompleted: boolean;
    importResult: { imported: number; updated: number; skipped: number } | null;
  } | null>(null);

  const orgsQuery = trpc.admin.orgs.useQuery(undefined);
  const validateMutation = trpc.admin.exerciseImport.validate.useMutation();
  const importMutation = trpc.admin.exerciseImport.import.useMutation();
  const exportMutation = trpc.admin.exerciseImport.export.useMutation();

  // Browse-first shell: previewWipe is the existing-exercises table source
  // whenever an org is selected (also drives wipe / bulk-delete candidates).
  const previewWipeQuery = trpc.admin.exerciseRemoval.previewWipe.useQuery(
    { orgId: selectedOrgId || '' },
    { enabled: !!selectedOrgId },
  );
  const removeSelectedMutation =
    trpc.admin.exerciseRemoval.removeSelected.useMutation();
  const removeWipeMutation =
    trpc.admin.exerciseRemoval.removeWipe.useMutation();
  const removeNotInListMutation =
    trpc.admin.exerciseRemoval.removeNotInList.useMutation();

  // Not-in-list preview for Import dialog replace mode. A mutation, not a
  // query — `rows` is the full validated file, and a GET query would put
  // that in the URL. Fired imperatively below whenever the dialog is on
  // replace with a validated file.
  const previewNotInListMutation =
    trpc.admin.exerciseRemoval.previewNotInList.useMutation();
  // Mirrors useQuery's per-key caching: `.mutate()` resets the mutation's
  // own `.data` to undefined synchronously on every call, so it can't tell
  // "first load" from "refetch of the same org/file" on its own. Track the
  // last resolved result here and only drop it when the org or file
  // actually changes — that's what keeps the preview showing cached
  // candidates (rather than flashing a spinner) across a background retry.
  const [previewNotInListResult, setPreviewNotInListResult] =
    useState<RemovalPreviewState | null>(null);
  const previewNotInListKeyRef = useRef<{
    orgId: string;
    teamId: string | null;
    exercises: ParsedExercise[];
  } | null>(null);

  const isPreviewNotInListEnabled =
    !!selectedOrgId &&
    isImportDialogOpen &&
    validationComplete &&
    exercises.length > 0 &&
    importRemovalMode === 'replace';

  useEffect(() => {
    if (!isPreviewNotInListEnabled || !selectedOrgId) return;

    const prevKey = previewNotInListKeyRef.current;
    const isSameKey =
      !!prevKey &&
      prevKey.orgId === selectedOrgId &&
      prevKey.teamId === selectedTeamId &&
      prevKey.exercises === exercises;
    if (!isSameKey) {
      // A genuinely new org, team, or re-validated file — the cached result
      // no longer describes what's being previewed, so drop it rather than
      // showing stale candidates while the new preview loads.
      setPreviewNotInListResult(null);
    }
    previewNotInListKeyRef.current = {
      orgId: selectedOrgId,
      teamId: selectedTeamId,
      exercises,
    };

    previewNotInListMutation.mutate(
      {
        orgId: selectedOrgId,
        teamId: selectedTeamId || undefined,
        rows: exercises.map(toExerciseInput),
      },
      { onSuccess: setPreviewNotInListResult },
    );
    // previewNotInListMutation is intentionally omitted — it's stable
    // per-render identity from useMutation and re-running on every render
    // caused by mutation state changes would defeat the point of gating on
    // isPreviewNotInListEnabled/selectedOrgId/selectedTeamId/exercises.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewNotInListEnabled, selectedOrgId, selectedTeamId, exercises]);

  // "Loading" (no cached result for this org/file yet — hide the checklist)
  // vs "fetching" (a request is in flight, cached candidates stay visible,
  // Continue stays disabled).
  const isPreviewNotInListLoading =
    previewNotInListMutation.isLoading && !previewNotInListResult;
  const isPreviewNotInListFetching = previewNotInListMutation.isLoading;

  const resetValidation = useCallback(() => {
    setExercises([]);
    setValidationComplete(false);
  }, []);

  const resetRemovalSelection = useCallback(() => {
    setSelectedExerciseIds([]);
    setIsConfirmDialogOpen(false);
    setRemovalError(null);
  }, []);

  const closeImportDialog = useCallback(() => {
    if (isConfirmDialogOpen || isRemovingRef.current) return;
    setIsImportDialogOpen(false);
    setFile(null);
    resetValidation();
    setImportRemovalModeState(null);
    confirmSnapshotRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetValidation, isConfirmDialogOpen]);

  const openImportDialog = useCallback(() => {
    if (!selectedOrgId) return;
    setIsImportDialogOpen(true);
  }, [selectedOrgId]);

  const handleOrgChange = useCallback(
    (orgId: string | null) => {
      setSelectedOrgId(orgId);
      setSelectedTeamId(null);
      resetValidation();
      resetRemovalSelection();
      setRemovalModeState(null);
      setImportRemovalModeState(null);
      setIsImportDialogOpen(false);
      setFile(null);
    },
    [resetValidation, resetRemovalSelection],
  );

  const handleTeamChange = useCallback(
    (teamId: string | null) => {
      setSelectedTeamId(teamId);
      resetValidation();
      // The existing-exercises table now scopes its visible rows to this
      // team (plus base library) — drop any selection made under the old
      // scope so a hidden, previously-selected exercise from another team
      // can't get bulk-removed via a stale ID.
      resetRemovalSelection();
      setImportRemovalModeState(null);
    },
    [resetValidation, resetRemovalSelection],
  );

  const handleDownloadExercises = useCallback(
    async (exerciseIds: string[]) => {
      if (!selectedOrgId) return;

      try {
        const data = await exportMutation.mutateAsync({
          orgId: selectedOrgId,
          exerciseIds,
        });

        const csvContent = exercisesToCSV(data);
        const org = orgsQuery.data?.find(
          (o: {
            id: string;
            name: string;
            teams: Array<{ id: string; name: string }>;
          }) => o.id === selectedOrgId,
        );
        const orgName = org?.name || 'org';
        const teamName = selectedTeamId
          ? org?.teams.find((t) => t.id === selectedTeamId)?.name
          : null;
        const nameParts = [
          orgName,
          teamName || 'base_library',
          'exercises',
        ].map((part) => part.replace(/[^a-z0-9]/gi, '_').toLowerCase());
        const filename = environment.VITE_ENV_TYPE === 'il4'
          ? `CUI_${nameParts.join('_')}.csv`
          : `${nameParts.join('_')}.csv`;

        // CUI exports: the designation can't live inside a CSV file, so gov
        // environments get the cover page zipped alongside the CSV.
        if (environment.VITE_ENV_TYPE === 'il4') {
          await downloadCuiExportZip(csvContent, filename);
        } else {
          downloadFile(csvContent, filename);
        }

        toast({
          title: 'Download successful',
          description: `Downloaded ${data.length} exercises`,
          severity: 'success',
        });
      } catch {
        toast({
          title: 'Download failed',
          description: 'Failed to download exercises',
          severity: 'error',
        });
      }
    },
    [selectedOrgId, selectedTeamId, exportMutation, orgsQuery.data, toast],
  );

  const processFile = useCallback(
    async (fileToProcess: File) => {
      if (!selectedOrgId) return;

      setIsProcessing(true);
      try {
        const text = await fileToProcess.text();
        const rawExercises = parseCSV(text);

        if (rawExercises.length === 0) {
          toast({
            title: 'No exercises found',
            description: 'The CSV file appears to be empty or invalid',
            severity: 'error',
          });
          setIsProcessing(false);
          return;
        }

        const validated = await validateMutation.mutateAsync({
          orgId: selectedOrgId,
          teamId: selectedTeamId || undefined,
          exercises: rawExercises.map(toExerciseInput),
        });

        setExercises(validated as ParsedExercise[]);
        setValidationComplete(true);
        // Safe default once a file validates — user can opt into removal modes.
        setImportRemovalModeState('import_only');

        const errorCount = validated.filter((e: ParsedExercise) =>
          e.issues.some((i: ValidationIssue) => i.type === 'error'),
        ).length;

        if (errorCount > 0) {
          toast({
            title: 'Validation errors found',
            description: `${errorCount} exercise(s) have errors that must be fixed before importing`,
            severity: 'error',
          });
        } else {
          toast({
            title: 'Validation complete',
            description: `${validated.length} exercise(s) ready to import`,
            severity: 'success',
          });
        }
      } catch (error) {
        toast({
          title: 'Error processing file',
          description:
            error instanceof Error
              ? error.message
              : 'Failed to validate exercises. Please try again.',
          severity: 'error',
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [selectedOrgId, selectedTeamId, validateMutation, toast],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) return;

      if (!selectedFile.name.endsWith('.csv')) {
        toast({
          title: 'Invalid file type',
          description: 'Please select a CSV file',
          severity: 'error',
        });
        return;
      }

      if (!selectedOrgId) {
        toast({
          title: 'No organization selected',
          description: 'Please select an organization first',
          severity: 'error',
        });
        return;
      }

      setFile(selectedFile);
      resetValidation();
      setImportRemovalModeState(null);
      // Do not clear table selectedExerciseIds — import dialog must not
      // clobber the browse-first table selection.
      await processFile(selectedFile);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [selectedOrgId, toast, processFile, resetValidation],
  );

  const handleImport = useCallback(async () => {
    if (!selectedOrgId) return;

    setIsImporting(true);
    try {
      const result = await importMutation.mutateAsync({
        orgId: selectedOrgId,
        teamId: selectedTeamId || undefined,
        exercises: exercises.map(toExerciseInput),
      });

      toast({
        title: 'Import successful',
        description: `${result.imported} exercise(s) created, ${result.updated} updated, ${result.skipped} skipped`,
        severity: 'success',
      });

      closeImportDialog();
      await previewWipeQuery.refetch();
    } catch (error) {
      toast({
        title: 'Import failed',
        description:
          error instanceof Error
            ? error.message
            : 'An error occurred during import',
        severity: 'error',
      });
    } finally {
      setIsImporting(false);
    }
  }, [
    selectedOrgId,
    selectedTeamId,
    exercises,
    importMutation,
    toast,
    closeImportDialog,
    previewWipeQuery,
  ]);

  const setImportRemovalMode = useCallback((mode: ImportRemovalMode) => {
    setImportRemovalModeState(mode);
  }, []);

  const toggleExerciseSelection = useCallback((exerciseId: string) => {
    setSelectedExerciseIds((prev) =>
      prev.includes(exerciseId)
        ? prev.filter((id) => id !== exerciseId)
        : [...prev, exerciseId],
    );
  }, []);

  const setAllExerciseSelection = useCallback(
    (exerciseIds: string[], selected: boolean) => {
      setSelectedExerciseIds((prev) => {
        if (!selected) {
          const drop = new Set(exerciseIds);
          return prev.filter((id) => !drop.has(id));
        }
        const next = new Set(prev);
        for (const id of exerciseIds) {
          next.add(id);
        }
        return Array.from(next);
      });
    },
    [],
  );

  const candidateExercises = useMemo(
    () => previewWipeQuery.data?.exercises ?? [],
    [previewWipeQuery.data],
  );

  const notInListCandidates = useMemo(
    () => previewNotInListResult?.exercises ?? [],
    [previewNotInListResult],
  );

  const notInListCount = previewNotInListResult?.count ?? 0;

  const removalPreview: RemovalPreviewState = useMemo(() => {
    if (removalMode === 'replace') {
      return {
        count: previewNotInListResult?.count ?? 0,
        exercises: previewNotInListResult?.exercises ?? [],
      };
    }

    if (removalMode === 'select_only') {
      const selected = candidateExercises.filter((exercise) =>
        selectedExerciseIds.includes(exercise.id),
      );
      return { count: selected.length, exercises: selected };
    }

    return { count: 0, exercises: [] };
  }, [
    removalMode,
    previewNotInListResult,
    candidateExercises,
    selectedExerciseIds,
  ]);

  const startBulkDelete = useCallback(() => {
    if (!selectedOrgId || selectedExerciseIds.length === 0) return;
    setRemovalModeState('select_only');
    setRemovalError(null);
    setIsConfirmDialogOpen(true);
  }, [selectedOrgId, selectedExerciseIds]);

  // Wipe soft-deletes every visible (non-deleted, non-cricket) exercise in
  // the selected scope — the whole org when no team is selected, or just the
  // selected team's library. The server recomputes the target set at commit
  // time, so this never trusts a stale id list.
  const startWipe = useCallback(() => {
    if (!selectedOrgId) return;
    setRemovalModeState('wipe');
    setRemovalError(null);
    setSelectedExerciseIds([]);
    setIsConfirmDialogOpen(true);
  }, [selectedOrgId]);

  const startImportConfirm = useCallback(() => {
    if (!selectedOrgId || !validationComplete) return;
    const fileHasErrors = exercises.some((e) =>
      e.issues.some((i) => i.type === 'error'),
    );
    if (fileHasErrors) return;
    if (importRemovalMode !== 'replace') {
      return;
    }

    if (notInListCount === 0) return;

    confirmSnapshotRef.current = {
      mode: 'replace',
      exerciseInputs: exercises.map(toExerciseInput),
      importCompleted: false,
      importResult: null,
    };
    setRemovalModeState('replace');
    setRemovalError(null);
    setIsConfirmDialogOpen(true);
  }, [
    selectedOrgId,
    validationComplete,
    importRemovalMode,
    notInListCount,
    exercises,
  ]);

  const cancelConfirm = useCallback(() => {
    setIsConfirmDialogOpen(false);
    setRemovalError(null);
    // Clear confirm-path mode so a leftover replace doesn't confuse a
    // subsequent table bulk-delete.
    setRemovalModeState(null);
    confirmSnapshotRef.current = null;
  }, []);

  const confirmRemoval = useCallback(async () => {
    if (!selectedOrgId || !removalMode) return;
    if (isRemovingRef.current) return;

    isRemovingRef.current = true;
    try {
      if (removalMode === 'replace') {
        const snapshot = confirmSnapshotRef.current;
        if (!snapshot || snapshot.mode !== 'replace') {
          setRemovalError('Confirm session expired. Close and try again.');
          return;
        }

        // Two-step: import must succeed before removal runs at all. Import
        // failures surface via toast (same as handleImport). After import
        // succeeds, Retry only re-runs removal — never a second import.
        let importResult = snapshot.importResult;
        if (!snapshot.importCompleted) {
          setIsImporting(true);
          setRemovalError(null);
          try {
            importResult = await importMutation.mutateAsync({
              orgId: selectedOrgId,
              teamId: selectedTeamId || undefined,
              exercises: snapshot.exerciseInputs,
            });
            snapshot.importCompleted = true;
            snapshot.importResult = importResult;
          } catch (error) {
            toast({
              title: 'Import failed',
              description:
                error instanceof Error
                  ? error.message
                  : 'An error occurred during import',
              severity: 'error',
            });
            return;
          } finally {
            setIsImporting(false);
          }
        }

        setIsRemoving(true);
        setRemovalError(null);
        try {
          const removalResult = await removeNotInListMutation.mutateAsync({
            orgId: selectedOrgId,
            teamId: selectedTeamId || undefined,
            rows: snapshot.exerciseInputs,
          });

          const imported = importResult?.imported ?? 0;
          const updated = importResult?.updated ?? 0;
          const skipped = importResult?.skipped ?? 0;
          toast({
            title: 'Import successful',
            description: `${imported} exercise(s) created, ${updated} updated, ${skipped} skipped, and ${removalResult.count} removed`,
            severity: 'success',
          });

          setIsConfirmDialogOpen(false);
          setRemovalModeState(null);
          confirmSnapshotRef.current = null;
          // Force-close import dialog even if confirm was open (bypass guard).
          setIsImportDialogOpen(false);
          setFile(null);
          resetValidation();
          setImportRemovalModeState(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          await previewWipeQuery.refetch();
        } catch (error) {
          setRemovalError(
            error instanceof Error
              ? error.message
              : 'Failed to remove exercises. Please try again.',
          );
        } finally {
          setIsRemoving(false);
        }
        return;
      }

      if (removalMode === 'wipe') {
        setIsRemoving(true);
        setRemovalError(null);
        try {
          const result = await removeWipeMutation.mutateAsync({
            orgId: selectedOrgId,
            teamId: selectedTeamId || undefined,
          });

          await previewWipeQuery.refetch();
          setIsConfirmDialogOpen(false);
          setRemovalModeState(null);
          toast({
            title: 'Wipe successful',
            description: `Removed ${result.count} exercise(s)`,
            severity: 'success',
          });
        } catch (error) {
          setRemovalError(
            error instanceof Error
              ? error.message
              : 'Failed to wipe library. Please try again.',
          );
        } finally {
          setIsRemoving(false);
        }
        return;
      }

      if (removalMode !== 'select_only') return;

      setIsRemoving(true);
      setRemovalError(null);
      try {
        await removeSelectedMutation.mutateAsync({
          orgId: selectedOrgId,
          exerciseIds: selectedExerciseIds,
          mode: 'select_only',
        });

        await previewWipeQuery.refetch();
        setIsConfirmDialogOpen(false);
        setRemovalModeState(null);
        setSelectedExerciseIds([]);
        toast({
          title: 'Removal successful',
          description: `Removed ${selectedExerciseIds.length} exercise(s)`,
          severity: 'success',
        });
      } catch (error) {
        setRemovalError(
          error instanceof Error
            ? error.message
            : 'Failed to remove exercises. Please try again.',
        );
      } finally {
        setIsRemoving(false);
      }
    } finally {
      isRemovingRef.current = false;
    }
  }, [
    selectedOrgId,
    selectedTeamId,
    removalMode,
    selectedExerciseIds,
    importMutation,
    removeSelectedMutation,
    removeWipeMutation,
    removeNotInListMutation,
    previewWipeQuery,
    toast,
    resetValidation,
  ]);

  const retryRemoval = confirmRemoval;

  const hasErrors = exercises.some((e) =>
    e.issues.some((i) => i.type === 'error'),
  );
  const errorCount = exercises.filter((e) =>
    e.issues.some((i) => i.type === 'error'),
  ).length;
  const createCount = exercises.filter((e) => e.action === 'create').length;
  const updateCount = exercises.filter((e) => e.action === 'update').length;
  const skipCount = exercises.filter((e) => e.action === 'skip').length;
  const hasExercisesToImport = createCount + updateCount > 0;

  return {
    file,
    exercises,
    isProcessing,
    isImporting,
    validationComplete,
    selectedOrgId,
    selectedTeamId,
    fileInputRef,
    isImportDialogOpen,
    openImportDialog,
    closeImportDialog,

    orgsQuery,
    previewWipeQuery,
    isPreviewNotInListLoading,
    isPreviewNotInListFetching,

    handleOrgChange,
    handleTeamChange,
    handleFileSelect,
    handleDownloadExercises,
    handleImport,

    hasErrors,
    errorCount,
    createCount,
    updateCount,
    skipCount,
    hasExercisesToImport,

    removalMode,
    importRemovalMode,
    setImportRemovalMode,
    candidateExercises,
    selectedExerciseIds,
    toggleExerciseSelection,
    setAllExerciseSelection,
    notInListCandidates,
    notInListCount,
    removalPreview,
    isConfirmDialogOpen,
    startBulkDelete,
    startWipe,
    startImportConfirm,
    cancelConfirm,
    isRemoving,
    removalError,
    confirmRemoval,
    retryRemoval,
  };
}

export type UseExerciseLibraryManagementResult = ReturnType<
  typeof useExerciseLibraryManagement
>;
